import {
  FormControlDependsOnSchema,
  FormControlDependsOnThenEnum,
  IFormControlDependsOnSchema,
  ObjectFromList
} from "@generation/core";
import { IDependsOn, INestedDependsOn } from "../../client/model/dependsOn";
import { DependsOnIfEnum } from "../../client/enums/dependsOnIfEnum";
import { DependsOnThenEnum } from "../../client/enums/dependsOnThenEnum";

export abstract class AbstractDependsOnMapper {
  abstract convert(item: IDependsOn, fieldType: string): FormControlDependsOnSchema<any>;
  abstract convertNested(item: INestedDependsOn, fieldType: string): FormControlDependsOnSchema<any>;

  getBaseProperties = (item: IDependsOn): IFormControlDependsOnSchema<any> => {
    return {
      properties: [item.property] as const,
      if: (values) => {
        const functionMap = {
          [DependsOnIfEnum.IsGiven]: this.isGiven,
          [DependsOnIfEnum.IsNotGiven]: this.isNotGiven,
          [DependsOnIfEnum.IsEqualToValue]: this.isEqualToValue,
          [DependsOnIfEnum.IsNotEqualToValue]: this.isNotEqualToValue,
          [DependsOnIfEnum.StartsWith]: this.startsWith,
          [DependsOnIfEnum.DoesNotStartWith]: this.doesNotStartWith
        };

        const selectedFunction = functionMap[item.if];

        if (selectedFunction) {
          return selectedFunction.call(this, values, item);
        }

        return false;
      },
      then: this.convertThen(item.then),
      isAbsolutePropertyPath: item.isAbsolutePath === true
    };
  };

  getBaseNestedProperties = (item: INestedDependsOn): IFormControlDependsOnSchema<any> => {
    return {
      properties: item.conditions?.map((condition) => condition.property) ?? [],
      if: (values) => {
        if (item.conditions) {
          return this.handleNested(values, item, "");
        }

        return false;
      },
      then: this.convertThen(item.then),
      isAbsolutePropertyPath: item.isAbsolutePath === true
    };
  };

  private handleNested = (
    values: ObjectFromList<readonly [string], string | any[] | null | undefined>,
    dependsOn: INestedDependsOn,
    _property: string
  ): boolean => {
    if (dependsOn.conditions) {
      const functionMap = {
        [DependsOnIfEnum.IsGiven]: this.isGiven,
        [DependsOnIfEnum.IsNotGiven]: this.isNotGiven,
        [DependsOnIfEnum.IsEqualToValue]: this.isEqualToValue,
        [DependsOnIfEnum.IsNotEqualToValue]: this.isNotEqualToValue,
        [DependsOnIfEnum.StartsWith]: this.startsWith,
        [DependsOnIfEnum.DoesNotStartWith]: this.doesNotStartWith
      };

      return dependsOn.conditions.every((dependsOn) => {
        const selectedFunction = functionMap[dependsOn.if];
        return selectedFunction.call(this, values, dependsOn, dependsOn.property);
      });
    }
    return false;
  };

  private isGiven = (
    values: ObjectFromList<readonly [string], string | any[] | null | undefined>,
    dependsOn: IDependsOn
  ): boolean => {
    return (
      values[dependsOn.property] !== null &&
      values[dependsOn.property] !== undefined &&
      values[dependsOn.property] !== ""
    );
  };

  private isNotGiven = (
    values: ObjectFromList<readonly [string], string | any[] | null | undefined>,
    dependsOn: IDependsOn
  ): boolean => {
    return (
      values[dependsOn.property] === null ||
      values[dependsOn.property] === undefined ||
      values[dependsOn.property] === ""
    );
  };

  private isEqualToValue = (
    values: ObjectFromList<readonly [string], string | any[] | null | undefined>,
    dependsOn: IDependsOn
  ): boolean => {
    if (dependsOn.value) {
      if (
        values[dependsOn.property] !== null &&
        values[dependsOn.property] !== undefined &&
        Array.isArray(values[dependsOn.property]) &&
        dependsOn.value
      ) {
        return (values[dependsOn.property] as any[]).includes(dependsOn.value);
      } else {
        return values[dependsOn.property] === dependsOn.value;
      }
    }

    return false;
  };

  private isNotEqualToValue = (
    values: ObjectFromList<readonly [string], string | any[] | null | undefined>,
    dependsOn: IDependsOn
  ): boolean => {
    if (Array.isArray(values[dependsOn.property]) && dependsOn.value) {
      return !(values[dependsOn.property] as any[]).includes(dependsOn.value);
    }
    return values[dependsOn.property] !== dependsOn.value;
  };

  private startsWith = (
    values: ObjectFromList<readonly [string], string | any[] | null | undefined>,
    dependsOn: IDependsOn
  ): boolean => {
    if (!dependsOn.value) {
      return false;
    }
    const startsWithValue = dependsOn.value.toLocaleLowerCase();

    if (Array.isArray(values[dependsOn.property])) {
      return (values[dependsOn.property] as any[]).find(
        (value) => value?.toLocaleLowerCase()?.startsWith(startsWithValue) ?? false
      );
    }
    return (values[dependsOn.property] as string)?.toLocaleLowerCase()?.startsWith(startsWithValue) ?? false;
  };

  private doesNotStartWith = (
    values: ObjectFromList<readonly [string], string | any[] | null | undefined>,
    dependsOn: IDependsOn
  ): boolean => {
    return !this.startsWith(values, dependsOn);
  };

  private convertThen = (then: DependsOnThenEnum | undefined): FormControlDependsOnThenEnum => {
    switch (then) {
      case DependsOnThenEnum.Disable:
        return FormControlDependsOnThenEnum.Disable;
      case DependsOnThenEnum.Hide:
        return FormControlDependsOnThenEnum.Hide;
      case DependsOnThenEnum.IsRequired:
        return FormControlDependsOnThenEnum.IsRequired;
      case DependsOnThenEnum.AddValidations:
        return FormControlDependsOnThenEnum.AddValidations;
      case DependsOnThenEnum.SetValue:
        return FormControlDependsOnThenEnum.SetValue;
      default:
        return FormControlDependsOnThenEnum.Hide;
    }
  };
}
